import { useCallback, useMemo } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';
import { useLocalConfirmDialog } from '@gef-ui/material/components/Dialog/ConfirmDialog';

import { refetchOriginalValues, sendAgenciesChangesToDb } from 'features/views/settings/marketPlaceAgencies/actions';
import {
	getAgencyManagersAndSubordinates,
	getMarketPlaceAgencies,
} from 'features/views/settings/marketPlaceAgencies/selectors';

export const ActionButtons = () => {
	const dispatch = useDispatch();
	const agencyManagersAndSubordinates = useSelector(getAgencyManagersAndSubordinates);
	const agencies = useSelector(getMarketPlaceAgencies);
	const anyAgencyWithoutAssignedManagers = useMemo(
		() => agencies.find((agency) => isNilOrEmpty(agency?.assignedManagers)),
		[agencies],
	);

	const [confirmDialogRevert, openConfirmRevert] = useLocalConfirmDialog({
		backdropClose: false,
		title: getLabelMessage('settings:marketPlaceAgencies.confirmRevert'),
		onOk: () => {
			dispatch(refetchOriginalValues());
		},
	});

	const [confirmDialogSave, openConfirmSave] = useLocalConfirmDialog({
		backdropClose: false,
		title: getLabelMessage('settings:marketPlaceAgencies.confirmSave'),
		content: getLabelMessage('settings:marketPlaceAgencies.someAgenciesAreWithoutManagers'),
		onOk: () => {
			dispatch(sendAgenciesChangesToDb());
		},
	});

	const handleSave = useCallback(() => {
		if (anyAgencyWithoutAssignedManagers) {
			openConfirmSave();
		} else {
			dispatch(sendAgenciesChangesToDb());
		}
	}, [dispatch, openConfirmSave, anyAgencyWithoutAssignedManagers]);

	const handleRevert = useCallback(() => {
		openConfirmRevert();
	}, [openConfirmRevert]);

	return (
		<Stack>
			{confirmDialogSave}
			{confirmDialogRevert}
			<Grid container spacing={2} justifyContent="flex-end">
				<Grid size={4}>
					<Button variant="contained" onClick={handleSave} fullWidth>
						{getLabelMessage('common:save')}
					</Button>
				</Grid>
				<Grid size={4}>
					<Button
						variant="contained"
						onClick={handleRevert}
						disabled={isNilOrEmpty(agencyManagersAndSubordinates)}
						fullWidth
					>
						{getLabelMessage('settings:marketPlaceAgencies.revertChanges')}
					</Button>
				</Grid>
			</Grid>
		</Stack>
	);
};

export default ActionButtons;
